import { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const labelClass = "block text-sm font-bold text-[#0A0A0A] mb-1.5";
const inputClass =
  "w-full bg-white border border-[#CBD5E1] rounded-lg px-3.5 py-2.5 text-sm font-medium text-[#0A0A0A] focus:outline-none focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#A8B5A2] transition-all";

const CONTACT_EMAIL = "hello@teachertoolslab.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      toast.error("Please add your email and a message.");
      return;
    }
    const subject = encodeURIComponent("Hello from TeacherToolsLab");
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    toast.success("Opening your email app...");
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20 w-full">
        <p className="text-sm font-bold uppercase tracking-widest text-[#64748B] mb-3">
          Contact
        </p>
        <h1
          data-testid="contact-title"
          className="font-display font-black text-4xl md:text-5xl tracking-tight mb-6"
        >
          Got a question or idea?
        </h1>
        <p className="text-lg text-[#1F2937] leading-relaxed mb-8 max-w-2xl">
          We love hearing from teachers. Drop us a note about a tool you wish
          existed, a bug you spotted, or a resource you'd like to see added.
        </p>

        <div className="bg-[#FAFAF9] border border-[#E5E7EB] rounded-2xl p-6 md:p-8 mb-8">
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="contact-email-link"
            className="inline-flex items-center gap-2 font-semibold text-[#0A0A0A] hover:underline"
          >
            <EnvelopeSimple size={20} weight="bold" />
            {CONTACT_EMAIL}
          </a>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8 shadow-sm"
        >
          <div>
            <label htmlFor="ct-name" className={labelClass}>
              Your name <span className="text-[#64748B] font-medium">(optional)</span>
            </label>
            <input
              id="ct-name"
              data-testid="contact-name"
              type="text"
              value={form.name}
              onChange={update("name")}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="ct-email" className={labelClass}>
              Email
            </label>
            <input
              id="ct-email"
              data-testid="contact-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              required
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="ct-message" className={labelClass}>
              Message
            </label>
            <textarea
              id="ct-message"
              data-testid="contact-message"
              rows={6}
              value={form.message}
              onChange={update("message")}
              required
              className={inputClass + " resize-y"}
            />
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white rounded-xl px-5 py-3 font-bold text-sm hover:bg-[#1E1E1E] transition-colors shadow-sm"
            >
              <PaperPlaneTilt size={18} weight="bold" />
              Send message
            </button>
            <p className="text-xs text-[#64748B] font-medium mt-2">
              This form opens your email app with the message pre-filled.
            </p>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
